import React, { useEffect, useMemo, useState } from "react";
import CtaComponent from "../../../components/reusable-ui/cta";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../redux/store";
import { cloneDeep, set } from "lodash";
import { allowEverything, allowOnlyNumber } from "../../../utils";
import { Modal } from "@mui/material";
import { useSearchParams } from 'react-router-dom'

import {
  createTransaction,
  deleteTrxnAttachment,
  downloadTrxnAttachment,
  updateCurrentTransactionData,
  updateTransaction,
  uploadTrxnAttachment,
} from "../../../redux/slices";
import {
  getErrorMessage,
  pos_errormap_without_label,
} from "../../../helpers/errors-helper";
import { toastError, toastSuccess } from "../../../utils/react-toaster";
import { toast } from "react-toastify";

import {
  CASHIER_FEATURE,
  CURRENT_DATE,
  EDIT_SUCCESS,
  FILE_UPLOAD_ERROR,
  REASON_DATA_MAP,
  TRANSACTION_TYPE_MAP,
  UPOADED_SUCCESSFULLY,
  USER_ROLE,
} from "./cash-managment-constants";
import LoaderContainer from "../../../components/reusable-ui/common-loader";
import AddEditHeader from "./add-edit-header";
import AddEditForm from "./add-edit-form";
import { convertArrayToObject } from "../../../helpers/catalog-helper";
import { isUserCashier } from "../../../helpers/isUserCashier";

interface Props {
  hideAddEditTransaction: (...args: any[]) => void;
  isEditMode?: boolean;
  updateTrxnIdForToast: (...args: any[]) => void;
  currentSelectedTrxnTypeId: string;
}

type TransactionType = {
  [key: string]: {
    typeId?: string;
    name?: string;
    val?: string;
  };
};

type ReasonType = {
  [key: string]: {
    reasonId?: string;
    name?: string;
  };
};

type paymentModeType = {
  [key: string]: {
    modeId?: string;
    name?: string;
  };
};

const AddEditTransaction = ({
  hideAddEditTransaction,
  isEditMode = false,
  updateTrxnIdForToast,
  currentSelectedTrxnTypeId = "",
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams(),
    [errorMsgMap, setErrMsgMap] = useState<any>({}),
    [selectedBranchId, setSelectedBranchId] = useState<string>(""),
    [paymentModeMap, setPaymentModeMap] = useState<paymentModeType>({}),
    [transactionConfig, setTransactionConfig] = useState<any>({
      typeId: searchParams.get('transactiontype') || currentSelectedTrxnTypeId,
    }),
    [transactionDate, setTransactionDate] = useState<any>(CURRENT_DATE),
    [enableEditBtn, setEnableEditBtn] = useState<boolean>(false),
    [showLoader, setShowLoader] = useState<boolean>(false),
    dispatch = useDispatch(),
    settlementId = useSelector((state: any) => state.businessReducer.midDetail),
    paymentModesMap = useSelector((state: any) => {
      return convertArrayToObject(
        state.businessReducer?.businessData?.paymentdefs?.filter((value:any) => value.settlement),
        'mode'
      );
    });

  const {
    paymentModesList,
    currentTransactionDetails,
    expenseReasons
  } = useSelector((state: RootState) => ({
    paymentModesList: state.businessReducer.businessData.paymentModes,
    currentTransactionDetails:
      state.accountingReducer.currentTransactionDetails,
    expenseReasons: state?.businessReducer?.businessData?.expenseReasons
  }));
  const expenseDataFromApi = useMemo(() => prepareExpenseData(expenseReasons), [expenseReasons]);
  const userData = useSelector((state: any) => state.businessReducer.userData);
  const allowedBusinessBranchesDataMap = convertArrayToObject(
    userData.permittedBranches,
    "branchId"
  );
  const isCashier = isUserCashier()

  const transactionTypeMap: TransactionType = TRANSACTION_TYPE_MAP;

  /**
   * Have put the reason hardcoded for now, will change in phase2
   */
  const reasonsDataMap: ReasonType = Object.keys(expenseDataFromApi)?.length ? expenseDataFromApi : REASON_DATA_MAP;

  useEffect(() => {
    preFillValues();
  }, [currentTransactionDetails, isEditMode, paymentModesList]);

  useEffect(() => {
    handleDefaultSelection();
  }, [allowedBusinessBranchesDataMap]);

  useEffect(() => {
    setPaymentMap();
  }, [paymentModesList]);

  /**
   * handles selection of transaction type according to the list we are on in table section
   * selects the branch id, we have already selected on cash table view
   */
  const handleDefaultSelection = () => {
    if (
      allowedBusinessBranchesDataMap &&
      Object.keys(allowedBusinessBranchesDataMap).length &&
      !selectedBranchId &&
      !transactionConfig?.brid &&
      !isEditMode
    ) {
      let firstObj: any = Object.values(allowedBusinessBranchesDataMap)[0];
      if (firstObj.branchId) {
        let config = cloneDeep(transactionConfig);
        config["brid"] = firstObj.branchId;
        setTransactionConfig(config);
        setSelectedBranchId(firstObj.branchId);
      }
    }
  };

  function prepareExpenseData(expenseReasons : Array<string> = []) {
    const expenseObj : Record<string, Record<string,string>> = {}
    expenseReasons?.forEach((reason : string, index : number) => {
      expenseObj[index + 1] = {
          name: reason,
          reasonId: String(index + 1)
         }
    })
    return expenseObj;
  }

  const setPaymentMap = () => {
    if (paymentModesList?.length) {
      let paymentMap = getPaymentMap(paymentModesList);
      setPaymentModeMap(paymentMap);
    }
  };

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(transactionConfig);
    set(dataToUpdate, fieldToUpdate, value);
    setTransactionConfig(dataToUpdate);
    isEditMode && setEnableEditBtn(true);
  };

  const handleFileChange = (event: any, fieldToUpdate: string) => {
    let fileSize = event.target?.files[0].size / 1024; // Convert to KB
    if (fileSize > 500) {
      let errorMap = cloneDeep(errorMsgMap);
      errorMap["file"] = FILE_UPLOAD_ERROR;
      setErrMsgMap(errorMap);
    } else {
      if (isEditMode) {
        setShowLoader(true);
        uploadAttachment(event.target.files[0]);
      } else {
        handleInputChange(event.target.files[0], fieldToUpdate);
      }
      let errorMap = cloneDeep(errorMsgMap);
      delete errorMap["file"];
      setErrMsgMap(errorMap);
    }
  };

  const TEXT_FIELD_CONFIG = {
    amountId: {
      label: "Amount",
      placeholder: "Enter Amount",
      regexCheck: allowOnlyNumber,
      customClass: "bg-white",
      parentClass: "mb-[6px]",
      value: transactionConfig?.amount,
      key: "amount",
      maxLength: 25,
      isDisabled: false,
      isMandatory: true,
    },
    receivedFrom: {
      label: "Received From",
      placeholder: "Enter Person name",
      regexCheck: allowEverything,
      customClass: "bg-white",
      parentClass: "mb-[6px]",
      value: transactionConfig?.receivedFrom,
      key: "receivedFrom",
      maxLength: 50,
      isDisabled: false,
      isMandatory: true,
    },
    receivedTo: {
      label: "Transferred To",
      placeholder: "Enter Person name",
      regexCheck: allowEverything,
      customClass: "bg-white",
      parentClass: "mb-[6px]",
      value: transactionConfig?.receivedTo,
      key: "receivedTo",
      maxLength: 50,
      isDisabled: false,
      isMandatory: true,
    },
    remark: {
      label: "Remark (optional)",
      placeholder: "Enter Remarks",
      regexCheck: allowEverything,
      customClass: "bg-white",
      parentClass: "mb-[6px]",
      value: transactionConfig?.remarks,
      key: "remarks",
      maxLength: 200,
      isDisabled: false,
      isMandatory: false,
    },
  };

  const DROPDOWN_CONFIGS = {
    trxType: {
      label: "Transaction Type",
      optionsMap: transactionTypeMap || {},
      placeholder: "Select transaction type",
      uniqueKey: "typeId",
      dropdownHeading: "Transaction Type",
      searchPlaceholder: "Search a transaction type",
      parentClass: "w-full",
      key: "typeId",
      preSelectedValues: transactionConfig?.typeId,
      onSubmit: (value: any) => handleInputChange(value[0], "typeId"),
      getListItemText: (data: any) => data?.name,
      isDisabled: isEditMode,
      isMandatory: true,
    },
    mode: {
      label: "Payment Mode",
      optionsMap: paymentModeMap || {},
      placeholder: "Select mode",
      uniqueKey: "modeId",
      dropdownHeading: "Mode",
      searchPlaceholder: "Search a mode",
      parentClass: "w-full",
      key: "modeId",
      preSelectedValues: transactionConfig?.modeId,
      onSubmit: (value: any) => handleInputChange(value[0], "modeId"),
      getListItemText: (data: any) => data?.name,
      isDisabled: false,
      isMandatory: true,
    },
    branch: {
      label: "Branch",
      optionsMap: allowedBusinessBranchesDataMap || {},
      placeholder: "Select Branch",
      uniqueKey: "branchId",
      dropdownHeading: "Branches",
      searchPlaceholder: "Search a branch",
      parentClass: "w-full",
      key: "brid",
      preSelectedValues: transactionConfig?.brid || selectedBranchId,
      onSubmit: (value: any) => handleInputChange(value[0], "brid"),
      getListItemText: (data: any) => data?.branchName,
      isDisabled: isCashier,
      isMandatory: true,
    },
    reason: {
      label: "Reason",
      optionsMap: reasonsDataMap || {},
      placeholder: "Select Reason",
      uniqueKey: "reasonId",
      dropdownHeading: "Reason",
      searchPlaceholder: "Search a reason",
      parentClass: "w-full",
      key: "reasonId",
      preSelectedValues: transactionConfig?.reasonId,
      onSubmit: (value: any) => handleInputChange(value[0], "reasonId"),
      getListItemText: (data: any) => data?.name,
      isDisabled: false,
      isMandatory: true,
    },
  };

  /**
   * this function helps in finding the id's whom value you pass with object for dropdowns.
   * @param objToTraverse
   * @param idKey
   * @param value
   * @param valToCheck
   * @returns
   */
  const getFieldVal = (
    objToTraverse: any,
    idKey: any,
    value: any,
    valToCheck: any
  ) => {
    let id;
    for (const key in objToTraverse) {
      if (objToTraverse[key][valToCheck] === value) {
        id = objToTraverse[key][idKey];
        break;
      }
    }
    return id;
  };

  /**
   * prefill form values, when viewed in edit mode
   */
  const preFillValues = () => {
    if (
      isEditMode &&
      currentTransactionDetails._id &&
      paymentModesList?.length
    ) {
      let config = cloneDeep(transactionConfig),
        paymentMap = getPaymentMap(paymentModesList);
      config["amount"] = currentTransactionDetails.amount;
      config["typeId"] = getFieldVal(
        transactionTypeMap,
        "typeId",
        currentTransactionDetails.transactionType,
        "val"
      );
      config["modeId"] = getFieldVal(
        paymentMap,
        "modeId",
        currentTransactionDetails.mode,
        "name"
      );
      config["reasonId"] = getFieldVal(
        reasonsDataMap,
        "reasonId",
        currentTransactionDetails.reason,
        "name"
      );
      config["brid"] = currentTransactionDetails.branchId;
      config["date"] = currentTransactionDetails.transactionDate;
      config["remarks"] = currentTransactionDetails.remarks;
      config["receivedFrom"] = currentTransactionDetails.receiveFrom;
      config["receivedTo"] = currentTransactionDetails.transferredTo;
      setTransactionDate(currentTransactionDetails.transactionDate);

      setTransactionConfig(config);
    }
  };

  /**
   * convert paymentMode arrays to index to object map, where object contains name as the string and modeId as index
   * @param paymentList
   * @returns
   */
  const getPaymentMap = (paymentList: any) => {
    let paymentMap: any = {};
    paymentList.map((paymentStr: string, idx: number) => {
      if(isUserCashier() && paymentModesMap[paymentStr.toLowerCase()])
       paymentMap[idx] = { modeId: String(idx), name: paymentStr };
      else {
        paymentMap[idx] = { modeId: String(idx), name: paymentStr };
      }
    });
    return paymentMap;
  };

  const resetTransactionForm = () => {
    if (isEditMode) {
      preFillValues();
    } else {
      setTransactionConfig({
        typeId: currentSelectedTrxnTypeId,
        brid: selectedBranchId,
        date: CURRENT_DATE,
      });
      setTransactionDate(CURRENT_DATE);
    }
  };

  /**
   * this function firsts clones the transactionConfig state and then creates a form data
   * the fields that are filled by user are stored in transactionConfig
   * @returns formData
   */
  const getPayloadData = () => {
    const formData: any = new FormData(),
      config = cloneDeep(transactionConfig);
    formData.append("transactionType", transactionTypeMap[config.typeId].val);
    formData.append("mode", paymentModeMap[config.modeId].name);
    formData.append("branchId", config.brid);
    formData.append("amount", config.amount);
    formData.append("transactionDate", config.date);
    formData.append("reason", reasonsDataMap[config.reasonId].name);
    formData.append("settlementId", isCashier ? settlementId : '')

    config.remarks && formData.append("remarks", config.remarks);
    config.receivedFrom &&
      config.typeId === "1" &&
      formData.append("receiveFrom", config.receivedFrom);
    config.receivedTo &&
      config.typeId === "2" &&
      formData.append("transferredTo", config.receivedTo);
    config.file && formData.append("attachment", config.file);

    return formData;
  };
  /**
   * Creation/Edit of form data and create/Edit transaction
   */
  const submitTransaction = () => {
    const dataToPost = getPayloadData();

    const callback = (isSuccess: boolean, response: any) => {
      if (response._id || response.success) {
        if (isEditMode) {
          toastSuccess(EDIT_SUCCESS);
        } else {
          fireCustomToast(response, dataToPost.get('transactionType'));
        }
        hideAddEditTransaction();
      } else {
        if (response.message) {
          let errMsg = getErrorMessage(
            pos_errormap_without_label,
            response.message
          );
          errMsg && toastError(errMsg);
        }
      }
    };
    if (isEditMode) {
      dispatch(
        updateTransaction({
          dataToPost,
          trxnId: currentTransactionDetails._id,
          callback,
        })
      );
    } else {
      dispatch(createTransaction({ dataToPost, callback }));
    }
  };

  const handleDateSelection = (dateObj: any) => {
    if (dateObj.date && dateObj.month && dateObj.year) {
      let dateStr = "";
      dateStr = `${
        dateObj.date < 10 ? `0${dateObj.date}/` : `${dateObj.date}/`
      }${dateObj.month < 10 ? `0${dateObj.month}/` : `${dateObj.month}/`}${
        dateObj.year
      }`;
      setTransactionDate(dateStr);

      let dataToUpdate = cloneDeep(transactionConfig);
      set(dataToUpdate, "date", dateStr);
      setTransactionConfig(dataToUpdate);
    }
  };

  const checkToEnableAdd = () => {
    if (
      transactionConfig.typeId &&
      transactionConfig.modeId &&
      transactionConfig.amount &&
      transactionConfig.brid &&
      transactionConfig.date &&
      transactionConfig.reasonId
    ) {
      if (
        (transactionConfig.typeId === "1" && transactionConfig.receivedFrom) ||
        (transactionConfig.typeId === "2" && transactionConfig.receivedTo) ||
        transactionConfig.typeId === "3"
      ) {
        return false;
      }
    }
    return true;
  };

  const checkToEnableEdit = () => {
    if (!checkToEnableAdd() && enableEditBtn) {
      return false;
    }
    return true;
  };

  const uploadAttachment = (fileData: any) => {
    let formData = new FormData();
    formData.append("activityId", currentTransactionDetails._id);
    formData.append(
      "mimeTypes",
      JSON.stringify(["image/jpeg", "image/png", "application/pdf"])
    );
    formData.append("maxFileSizeInKB", "10240");
    formData.append("file", fileData);

    const dataToPost = formData;

    const callback = (isSuccess: boolean) => {
      if (isSuccess) {
        setShowLoader(false);
        toastSuccess(UPOADED_SUCCESSFULLY);
        hideAddEditTransaction();
      }
    };
    dispatch(
      uploadTrxnAttachment({
        dataToPost,
        trxnId: currentTransactionDetails._id,
        callback,
      })
    );
  };

  const downloadAttachment = () => {
    const callback = (isSuccess: boolean, response: any) => {
      if (isSuccess && response?.fileURL) {
        window.location.href = `${response?.fileURL}`;
      }
    };
    let dataToPost = {
      id: currentTransactionDetails._id,
      attachmentId: currentTransactionDetails.attachments[0].id,
      fileName: currentTransactionDetails.attachments[0].fileName,
    };
    dispatch(downloadTrxnAttachment({ dataToPost, callback }));
  };

  const deleteAttachment = () => {
    const callback = (isSuccess: boolean) => {
      if (isSuccess) {
        let data = cloneDeep(currentTransactionDetails);
        data.attachments = [];
        dispatch(updateCurrentTransactionData(data));
      }
    };
    let dataToPost = {
      id: currentTransactionDetails._id,
      attachmentId: currentTransactionDetails.attachments[0].id,
      fileName: currentTransactionDetails.attachments[0].fileName,
    };
    dispatch(deleteTrxnAttachment({ dataToPost, callback }));
  };

  const fireCustomToast = (response: any, type: any) => {
    const options: any = {
      onClick: () => {
        dispatch(updateCurrentTransactionData(response));
        updateTrxnIdForToast(response._id);
      },
      hideProgressBar: true,
      position: "bottom-right",
      theme: "colored",
    };
    toast.success(
      ({ closeToast }) => (
        <div>
          <p>{`${type} Added`}</p>
          <p style={{ textDecoration: "underline" }}>Click Here to View</p>
        </div>
      ),
      options
    );
  };

  function renderHeader() {
    return (
      <AddEditHeader
        hideAddEditTransaction={hideAddEditTransaction}
        isEditMode={isEditMode}
        resetTransactionForm={resetTransactionForm}
        submitTransaction={submitTransaction}
        checkToEnableEdit={checkToEnableEdit}
        checkToEnableAdd={checkToEnableAdd}
      />
    );
  }

  function getFormView() {
    return (
      <AddEditForm handleDateSelection={handleDateSelection}
        isEditMode = {isEditMode}
        currentTransactionDetails = {currentTransactionDetails}
        transactionDate = {transactionDate}
        text_Config = {TEXT_FIELD_CONFIG}
        handleInputChange = {handleInputChange}
        errorMsgMap = {errorMsgMap}
        dropdown_Config = {DROPDOWN_CONFIGS}
        transactionConfig = {transactionConfig}
        downloadAttachment = {downloadAttachment}
        deleteAttachment = {deleteAttachment}
        handleFileChange = {handleFileChange}
        isCashier = {isCashier}
      />
    )
  }

  const getBtnView = () => {
    return (
      <div className="save-mb-btn px-13 py-10 bg-white shadow-[0px_-3px_6px_#206DFD1C] fixed left-0 right-0 bottom-0">
        {isEditMode ? (
          <CtaComponent
            ctaText={"Edit Transaction"}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
            onCtaClick={submitTransaction}
            customClass={"p-10 w-full"}
            isDisabled={checkToEnableEdit()}
          ></CtaComponent>
        ) : (
          <CtaComponent
            ctaText={"Save Transaction"}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
            onCtaClick={submitTransaction}
            customClass={"p-10 w-full"}
            isDisabled={checkToEnableAdd()}
          ></CtaComponent>
        )}
      </div>
    )
  }

  return (
    <>
      {showLoader && <LoaderContainer />}
      {isMobileDevice() ? (
        <Modal open={true} onClose={hideAddEditTransaction}>
          <div
            className={`add-edit-transaction-container absolute w-full h-full
          overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2  bg-white`}
          >
            <div className="h-full overflow-y-scroll pb-70">
              {renderHeader()}
              {getFormView()}
            </div>
              {getBtnView()}
          </div>
        </Modal>
      ) : (
        <div
          className={`add-edit-transaction-container w-[766px] border border-[#DDDDDD] rounded-[12px] mx-auto`}
        >
          {renderHeader()}
          {getFormView()}
        </div>
      )}
    </>
  );
};

export default AddEditTransaction;
